import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  Divider,
  useTheme,
  alpha,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";
import { useAudioRecorder } from "react-audio-voice-recorder";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { usePronunciationAnalyze } from "@/services-react-query/queries";
import { IPronunciationAnalyzeOutput } from "@/services-react-query/api/practice.service";
import { onPlayReadStr, hasVowelPronunciationError } from "@/helpers";
import PhonemeBreakdown from "@/components/PhonemeBreakdown";
import WaveformRecorder from "@/components/WaveformRecorder";
import PronunciationGuideDisplay from "@/components/PronunciationGuideDisplay";

interface SentencePronunciationProps {
  sentence: string;
  onResult: (result: IPronunciationAnalyzeOutput) => void;
  existingResult?: IPronunciationAnalyzeOutput;
  dataWordSearch?: any;
}

const SCORE_THEMES = [
  { min: 85, color: "#2e7d32", bg: "rgba(46, 125, 50, 0.08)" },
  { min: 70, color: "#1976d2", bg: "rgba(25, 118, 210, 0.1)" },
  { min: 55, color: "#ef6c00", bg: "rgba(239, 108, 0, 0.08)" },
  { min: 0, color: "#c62828", bg: "rgba(198, 40, 40, 0.08)" },
];

const getScoreTheme = (score: number) => {
  return SCORE_THEMES.find((theme) => score >= theme.min) || SCORE_THEMES[SCORE_THEMES.length - 1];
};

const SentencePronunciation: React.FC<SentencePronunciationProps> = ({
  sentence,
  onResult,
  existingResult,
  dataWordSearch,
}) => {
  const theme = useTheme();
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  const [audioRecord, setAudioRecord] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);

  // Target practice text selection (Default: full sentence)
  const [targetText, setTargetText] = useState<string>(sentence);

  useEffect(() => {
    setTargetText(sentence);
    setAudioRecord(null);
    setSelectedWordIndex(null);
  }, [sentence]);

  const recorderControls = useAudioRecorder(
    {
      noiseSuppression: true,
      echoCancellation: true,
    },
    (err) => console.error(err)
  );

  useEffect(() => {
    if (recorderControls.recordingBlob) {
      const url = URL.createObjectURL(recorderControls.recordingBlob);
      setAudioRecord(url);
    }
  }, [recorderControls.recordingBlob]);

  const { mutate: analyzePronunciation, isLoading: loadingAnalyze } = usePronunciationAnalyze({
    onSuccess: (data) => {
      onResult(data);
      setIsAnalyzing(false);
    },
    onError: (error) => {
      console.error("Pronunciation analysis error:", error);
      setIsAnalyzing(false);
    },
  });

  const handleAnalyze = useCallback(async () => {
    if (!audioRecord) return;
    setIsAnalyzing(true);
    try {
      const blob = await (await fetch(audioRecord)).blob();
      const timeStamp = new Date(Date.now()).toISOString().replace(/:/g, "-").replace(/\./g, "-");
      const file = new File([blob], `${timeStamp}.webm`, { type: "audio/webm" });

      // Send the specific targetText to SpeechAce API for focused evaluation
      analyzePronunciation({
        sentence: targetText,
        audioFile: file,
      });
    } catch (error) {
      console.error("Error analyzing pronunciation:", error);
      setIsAnalyzing(false);
    }
  }, [audioRecord, targetText, analyzePronunciation]);

  const handleReset = useCallback(() => {
    setAudioRecord(null);
    setSelectedWordIndex(null);
    onResult(null as any);
  }, [onResult]);

  // Text selection handler: Allows user to drag mouse/highlight any portion of text to practice
  const handleMouseUpSelection = useCallback(() => {
    const sel = window.getSelection();
    const rawSelected = sel ? sel.toString().trim() : "";
    if (!rawSelected || rawSelected.length === 0) return;

    // Clean surrounding punctuation or spaces
    const cleaned = rawSelected.replace(/^[.,;:!?\s]+|[.,;:!?\s]+$/g, "");
    if (cleaned.length > 0 && sentence.toLowerCase().includes(cleaned.toLowerCase())) {
      setTargetText(cleaned);
      handleReset();
    }
  }, [sentence, handleReset]);

  // Generate sub-sentence clause options from full sentence
  const clauses = useMemo(() => {
    return sentence
      .split(/(?<=[,;:.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }, [sentence]);

  const isFullSentence = useMemo(() => {
    return targetText.trim().toLowerCase() === sentence.trim().toLowerCase();
  }, [targetText, sentence]);

  const activeWords = useMemo(() => {
    return (targetText || sentence).split(" ");
  }, [targetText, sentence]);

  const overallScore = existingResult?.overallScore ?? 0;
  const accuracyScore = existingResult?.accuracyScore ?? 0;
  const completenessScore = existingResult?.completenessScore ?? 0;
  const fluencyScore = existingResult?.fluencyScore ?? 0;

  const scoreMetrics = useMemo(
    () => [
      { label: "Tổng quan", value: overallScore, color: "#2e7d32" },
      { label: "Chính xác", value: accuracyScore, color: "#2c80d3" },
      { label: "Hoàn thành", value: completenessScore, color: "#9c27b0" },
      { label: "Trôi chảy", value: fluencyScore, color: "#ef6c00" },
    ],
    [overallScore, accuracyScore, completenessScore, fluencyScore]
  );

  return (
    <Stack spacing={2}>
      {/* Selectable Sentence Card with Mouse Highlight feature */}
      <Paper
        elevation={0}
        onMouseUp={handleMouseUpSelection}
        sx={{
          p: 1.8,
          borderRadius: "16px",
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : alpha("#10B981", 0.25)}`,
          userSelect: "text !important",
          WebkitUserSelect: "text !important",
          cursor: "text",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: alpha("#10B981", 0.5),
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: "#10B981",
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              fontSize: "0.8rem",
            }}
          >
            <TouchAppIcon sx={{ fontSize: 16 }} /> Dùng chuột bôi đen đoạn văn bản dưới đây để chọn luyện phát âm đoạn
            đó:
          </Typography>
          <IconButton
            size="small"
            onClick={() => onPlayReadStr(targetText || sentence, undefined, "en-US")}
            title="Nghe mẫu đoạn đã chọn"
            sx={{
              color: "#10B981",
              p: 0.5,
              bgcolor: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.08)",
              "&:hover": { bgcolor: "#10B981", color: "#fff" },
            }}
          >
            <VolumeUpIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            lineHeight: 1.55,
            fontSize: "1.1rem",
            color: isDark ? "#ffffff" : "#0f172a",
            userSelect: "text !important",
            WebkitUserSelect: "text !important",
            "&::selection": {
              bgcolor: "#10B981",
              color: "#fff",
            },
          }}
        >
          {sentence}
        </Typography>
      </Paper>

      {/* Target Selection Alert / Quick Scope Selector Bar */}
      {!isFullSentence ? (
        <Alert
          severity="info"
          icon={<TrackChangesIcon sx={{ color: "#10B981", fontSize: 20 }} />}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setTargetText(sentence);
                handleReset();
              }}
              startIcon={<RestartAltIcon fontSize="small" />}
              sx={{ fontWeight: 700, textTransform: "none", fontSize: "0.78rem", py: 0.3 }}
            >
              Khôi phục cả câu
            </Button>
          }
          sx={{
            py: 0.5,
            px: 1.8,
            borderRadius: "12px",
            bgcolor: isDark ? "rgba(16,185,129,0.12)" : "rgba(16,185,129,0.06)",
            border: `1px solid ${alpha("#10B981", 0.3)}`,
            color: isDark ? "#A7F3D0" : "#047857",
            fontWeight: 600,
            fontSize: "0.85rem",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              flexWrap: "wrap",
              py: 0,
            },
          }}
        >
          <span>🎯 Đang chọn luyện phát âm đoạn bôi đen:</span>
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              bgcolor: isDark ? "rgba(16,185,129,0.25)" : "rgba(16,185,129,0.12)",
              px: 1.2,
              py: 0.25,
              borderRadius: "5px",
              color: "#10B981",
              fontSize: "0.85rem",
            }}
          >
            "{targetText}"
          </Typography>
        </Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            px: 2,
            borderRadius: "14px",
            bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(16, 185, 129, 0.03)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(16, 185, 129, 0.15)"}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 800, color: "#10B981", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <TrackChangesIcon sx={{ fontSize: 16 }} /> Phạm vi nhanh:
          </Typography>

          <Chip
            label="Cả câu"
            size="small"
            onClick={() => {
              setTargetText(sentence);
              handleReset();
            }}
            sx={{
              fontWeight: 700,
              fontSize: "12px",
              bgcolor: "#10B981",
              color: "#fff",
              cursor: "pointer",
            }}
          />

          {clauses.length > 1 &&
            clauses.map((clause, cIdx) => (
              <Chip
                key={cIdx}
                label={`Vế ${cIdx + 1}`}
                size="small"
                onClick={() => {
                  setTargetText(clause);
                  handleReset();
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "12px",
                  bgcolor: "transparent",
                  color: "text.secondary",
                  border: `1px solid ${alpha("#10B981", 0.35)}`,
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: alpha("#10B981", 0.1),
                  },
                }}
              />
            ))}
        </Paper>
      )}

      {/* Reusable Pronunciation & Intonation Guide Display */}
      <PronunciationGuideDisplay
        text={sentence}
        autoLoad={true}
        selectedTargetText={targetText}
        onSelectTargetText={(selected) => {
          setTargetText(selected);
          handleReset();
        }}
      />

      {/* Reusable Waveform Recorder */}
      <WaveformRecorder
        isRecording={recorderControls.isRecording}
        recordingTime={recorderControls.recordingTime}
        audioUrl={audioRecord}
        isAnalyzing={isAnalyzing || loadingAnalyze}
        onStartRecording={() => recorderControls.startRecording()}
        onStopRecording={() => recorderControls.stopRecording()}
        onAnalyze={handleAnalyze}
        onReset={handleReset}
        maxRecordingTime={60}
        subtitleText={`🎤 Đang ghi âm bài luyện: "${targetText}"`}
        disableAnalyze={loadingAnalyze}
      />

      {/* Results Display */}
      {existingResult && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "24px",
            background: isDark ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.success.main, 0.03),
            border: `1px solid ${
              isDark ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.success.main, 0.1)
            }`,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(46, 125, 50, 0.05)",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#059669", fontSize: "18px", textTransform: "uppercase", letterSpacing: 1 }}
            >
              📊 Báo cáo phân tích: "{targetText}"
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            {scoreMetrics.map((metric) => (
              <Box
                key={metric.label}
                sx={{
                  flex: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: "rgba(255,255,255,0.65)",
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: "13px", color: "#546e7a", fontWeight: 600 }}>{metric.label}</Typography>
                  <Chip
                    label={`${Math.round(metric.value)} / 100`}
                    size="small"
                    sx={{
                      backgroundColor: metric.color,
                      color: "white",
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(metric.value, 100)}
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 5,
                    backgroundColor: "rgba(0,0,0,0.08)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: metric.color,
                      borderRadius: 5,
                    },
                  }}
                />
              </Box>
            ))}
          </Stack>

          <Divider sx={{ mb: 2, borderColor: "#e0ebe0" }} />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            {activeWords.map((word, wordIndex) => {
              const wordResult = existingResult.words.find(
                (w) => w.word.toLowerCase() === word.replace(/[,.]/g, "").toLowerCase()
              );
              const isVowelErr = hasVowelPronunciationError(wordResult?.phonemes);
              const score = wordResult?.score ?? (wordResult?.isCorrect ? 80 : 0);
              const themeScore = isVowelErr ? { color: "#ef5350", bg: "rgba(239, 83, 80, 0.08)" } : getScoreTheme(score);
              const isCorrect = (wordResult?.isCorrect ?? true) && !isVowelErr;
              const ipa = wordResult?.ipa;
              const userIpa = wordResult?.userIpa;
              const isSelected = selectedWordIndex === wordIndex;

              return (
                <Paper
                  key={wordIndex}
                  elevation={isSelected ? 4 : 0}
                  onClick={() => setSelectedWordIndex(wordIndex)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: isSelected
                      ? isCorrect
                        ? "rgba(46, 125, 50, 0.15)"
                        : "rgba(211, 47, 47, 0.15)"
                      : isCorrect
                        ? "rgba(46, 125, 50, 0.05)"
                        : "rgba(211, 47, 47, 0.05)",
                    border: `1.5px solid ${
                      isSelected ? (isCorrect ? "#2e7d32" : "#c62828") : isCorrect ? "#66bb6a" : "#ef5350"
                    }`,
                    minWidth: "70px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    transform: isSelected ? "scale(1.05)" : "none",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 2px 8px ${isCorrect ? "rgba(76, 175, 80, 0.15)" : "rgba(244, 67, 54, 0.15)"}`,
                      borderColor: isCorrect ? "#4caf50" : "#f44336",
                      backgroundColor: isCorrect ? "rgba(46, 125, 50, 0.1)" : "rgba(211, 47, 47, 0.1)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", width: "100%" }}>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: themeScore.color,
                        mb: ipa ? 0.5 : 0,
                        textAlign: "center",
                      }}
                    >
                      {word}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${Math.round(score)}`}
                    size="small"
                    sx={{
                      mb: ipa ? 0.3 : 0,
                      fontSize: "11px",
                      fontWeight: 600,
                      height: 20,
                      backgroundColor: `${themeScore.color}22`,
                      color: themeScore.color,
                    }}
                  />
                  {ipa && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "12px",
                        fontStyle: "italic",
                        color: "#7b1fa2",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {ipa}
                    </Typography>
                  )}
                  {userIpa && userIpa !== ipa && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        fontStyle: "italic",
                        color: "#d32f2f",
                        mt: 0.5,
                        textAlign: "center",
                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 0.5,
                      }}
                    >
                      Bạn: {userIpa}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Box>

          {selectedWordIndex !== null && existingResult.words[selectedWordIndex] && (
            <Box sx={{ mb: 2, animation: "fadeIn 0.3s ease-in-out" }}>
              <PhonemeBreakdown
                word={activeWords[selectedWordIndex]}
                phonemes={
                  existingResult.words.find(
                    (w) => w.word.toLowerCase() === activeWords[selectedWordIndex].replace(/[,.]/g, "").toLowerCase()
                  )?.phonemes || []
                }
                userAudioUrl={audioRecord || undefined}
                extent={
                  existingResult.words.find(
                    (w) => w.word.toLowerCase() === activeWords[selectedWordIndex].replace(/[,.]/g, "").toLowerCase()
                  )?.extent
                }
                uid={
                  dataWordSearch?.items?.find(
                    (item: any) => item.word === activeWords[selectedWordIndex].replace(/[,.]/g, "").toLowerCase()
                  )?.uid
                }
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "center",
              pt: 1.5,
              borderTop: "1px solid #e0ebe0",
            }}
          >
            <Stack direction="row" spacing={2}>
              {SCORE_THEMES.map((themeItem) => (
                <Stack key={themeItem.min} direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: themeItem.color,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: themeItem.color, fontWeight: 500 }}>
                    {themeItem.min}+ điểm
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontSize: "11px",
                fontStyle: "italic",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <VolumeUpIcon sx={{ fontSize: 12 }} />
              Click vào từng từ để xem chi tiết
            </Typography>
          </Box>
        </Paper>
      )}
    </Stack>
  );
};

export default memo(SentencePronunciation);
